import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signIn } from '../lib/supabase/auth';
import { getOnboardingStatus } from '../lib/api/userApi';
import { AiOutlineLogin } from 'react-icons/ai';

const SignInPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Check for sign-out status in URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const status = searchParams.get('status');
    
    if (status === 'signed-out') {
      setSuccess('You have been successfully signed out.');
    }
  }, [location]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const { error } = await signIn(email, password);
      
      if (error) {
        setError(error.message);
        setLoading(false);
        return;
      }
      
      // Check onboarding status
      try {
        const { data: onboardingData, error: onboardingError } = await getOnboardingStatus();
        
        if (onboardingError) {
          console.error('Error checking onboarding status:', onboardingError);
          // Continue with sign in process even if onboarding check fails
        } else if (onboardingData && !onboardingData.hasCompletedOnboarding) {
          // If onboarding is not completed, redirect to onboarding page
          console.log('User has not completed onboarding, redirecting...');
          navigate('/onboarding');
          return;
        }
      } catch (err) {
        console.error('Error in onboarding check:', err);
        // Continue with sign in process even if onboarding check fails
      }
      
      // Redirect to home page on successful sign in
      navigate('/');
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Sign in error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
      <div className="auth-form__container w-full max-w-md p-8 bg-white rounded-lg shadow-md">
        <div className="auth-form__header mb-8 text-center">
          <img 
            src="/images/penguin-logo.svg" 
            alt="PokketCoach Logo" 
            className="auth-form__logo w-16 h-16 mx-auto mb-2"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Ccircle cx='20' cy='20' r='18' fill='%23C0E1F7' stroke='%230A84FF' stroke-width='2'/%3E%3Cpath d='M15 15 Q20 10, 25 15 Q30 20, 25 25 Q20 30, 15 25 Q10 20, 15 15' fill='%230A84FF'/%3E%3Ccircle cx='17' cy='17' r='2' fill='white'/%3E%3Ccircle cx='23' cy='17' r='2' fill='white'/%3E%3C/svg%3E";
            }}
          />
          <h1 className="auth-form__title text-2xl font-bold text-gray-800">Sign In to PokketCoach</h1>
          <p className="auth-form__subtitle text-gray-600 mt-2">Welcome back! Please enter your details</p>
        </div>

        {error && (
          <div className="auth-form__error mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="auth-form__success mb-4 p-3 bg-green-100 text-green-700 rounded-md text-sm">
            {success}
          </div>
        )}

        <form onSubmit={handleSignIn} className="auth-form">
          <div className="auth-form__field mb-4">
            <label htmlFor="email" className="auth-form__label block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="auth-form__input w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Enter your email"
            />
          </div>

          <div className="auth-form__field mb-6">
            <label htmlFor="password" className="auth-form__label block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="auth-form__input w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Enter your password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="auth-form__submit-button w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-md transition-colors flex items-center justify-center"
          >
            {loading ? (
              <span className="auth-form__spinner inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
            ) : (
              <AiOutlineLogin className="mr-2" />
            )}
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div className="auth-form__footer mt-6 text-center text-sm">
          <p className="auth-form__redirect-text text-gray-600">
            Don't have an account?{' '}
            <Link to="/signup" className="auth-form__redirect-link text-blue-500 hover:text-blue-700 font-medium">
              Sign Up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignInPage; 